/**
 * Pure helper that lays out a month as a Monday-first grid. The view colours
 * the cells from the API; this file only knows the shape of the month.
 */

import { toKey } from './format';

export interface CalendarCell {
    /** YYYY-MM-DD, or null for a leading blank before the 1st. */
    date: string | null;
    /** Day of month, or null for a blank. */
    day: number | null;
}

export interface CalendarGrid {
    /** Number of blank cells before the 1st (0 when the month starts on a Monday). */
    lead: number;
    /** Days in the month (28..31). */
    length: number;
    cells: CalendarCell[];
}

/** Monday-first offset of a weekday: Mon 0 .. Sun 6. */
export function mondayIndex(jsWeekday: number): number {
    return (jsWeekday + 6) % 7;
}

export function daysInMonth(year: number, month0: number): number {
    return new Date(year, month0 + 1, 0).getDate();
}

/**
 * Build the grid for a month. `month0` is 0-based like Date#getMonth.
 * Trailing blanks are not padded; the proto's grid simply ends on the last day.
 */
export function buildCalendarCells(year: number, month0: number): CalendarGrid {
    const first = new Date(year, month0, 1);
    const lead = mondayIndex(first.getDay());
    const length = daysInMonth(year, month0);
    const cells: CalendarCell[] = [];
    for (let i = 0; i < lead; i++) cells.push({ date: null, day: null });
    for (let d = 1; d <= length; d++) {
        cells.push({ date: toKey(new Date(year, month0, d)), day: d });
    }
    return { lead, length, cells };
}

export type ScoreClass = 'g5' | 'g3' | 'g1' | 'g0';

/**
 * Cell colour for a logged day. Mirrors the proto: 5 full, 3-4 most, 1-2
 * some, otherwise "some" if the day was touched at all, else none.
 * The score itself always comes from the API.
 */
export function scoreClass(score: number, touched: boolean): ScoreClass {
    if (score >= 5) return 'g5';
    if (score >= 3) return 'g3';
    if (score >= 1) return 'g1';
    return touched ? 'g1' : 'g0';
}
